import { useEffect, useMemo, useState } from 'react';
import apiClient from '../lib/api';
import { Loader2, Search } from 'lucide-react';

type MembershipStatus = 'pending_clinical' | 'active' | 'paused' | 'past_due' | 'cancelled';

interface ManagedMembership {
  _id: string;
  tier: 1 | 2 | 3;
  productCount: 1 | 2 | 4;
  pricePerCycleCents: number;
  cycleLengthDays: number;
  status: MembershipStatus;
  paymentMethodId?: string;
  selectedProductIds: Array<{ _id: string; displayName: string; drugName: string } | string>;
  patientId: {
    _id: string;
    firstName: string;
    lastName: string;
    phone: string;
    email?: string;
    address?: { city?: string; state?: string };
    screeningAnswers?: Record<string, any>;
  } | string;
  nextBillingDate?: string;
  createdAt: string;
}

const STATUS_FILTERS: Array<{ key: 'all' | MembershipStatus; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'past_due', label: 'Past due' },
  { key: 'paused', label: 'Paused' },
  { key: 'pending_clinical', label: 'Pending card' }
];

const STATUS_PILL: Record<MembershipStatus, string> = {
  pending_clinical: 'bg-amber-50 text-amber-800 border-amber-200',
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  paused: 'bg-gray-50 text-gray-700 border-gray-200',
  past_due: 'bg-red-50 text-red-800 border-red-200',
  cancelled: 'bg-gray-50 text-gray-500 border-gray-200'
};

const STATUS_LABEL: Record<MembershipStatus, string> = {
  pending_clinical: 'Awaiting card',
  active: 'Active',
  paused: 'Paused',
  past_due: 'Past due',
  cancelled: 'Cancelled'
};

export default function PhysicianMembershipReviews() {
  const [memberships, setMemberships] = useState<ManagedMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | MembershipStatus>('all');
  const [search, setSearch] = useState('');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<{ success: boolean; memberships: ManagedMembership[] }>(
        '/memberships'
      );
      setMemberships(res.memberships || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = memberships;
    if (statusFilter !== 'all') {
      list = list.filter(m => m.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(m => {
        const p = typeof m.patientId === 'object' ? m.patientId : null;
        return (
          (p?.firstName + ' ' + p?.lastName).toLowerCase().includes(q) ||
          p?.email?.toLowerCase().includes(q) ||
          p?.phone?.includes(q)
        );
      });
    }
    return list;
  }, [memberships, statusFilter, search]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: memberships.length };
    for (const m of memberships) c[m.status] = (c[m.status] || 0) + 1;
    return c;
  }, [memberships]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Memberships</h1>
      <p className="text-sm text-gray-500 mb-6">
        Active members in your DTP membership program. Memberships activate automatically after the patient saves a card; you don't need to approve each one.
      </p>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm border transition ${
              statusFilter === f.key
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
            }`}
          >
            {f.label}{counts[f.key] != null && f.key !== 'all' ? ` (${counts[f.key]})` : ''}{f.key === 'all' ? ` (${counts.all})` : ''}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, phone…"
            className="pl-9 pr-3 py-1.5 border border-gray-200 rounded-md text-sm w-64"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500 border border-dashed border-gray-200 rounded-lg">
          No memberships match this filter.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left py-2 px-3">Patient</th>
                <th className="text-left py-2 px-3">Tier</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Selections</th>
                <th className="text-left py-2 px-3">Next charge</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(m => {
                const patient = typeof m.patientId === 'object' ? m.patientId : null;
                const products = (m.selectedProductIds || []).map(p =>
                  typeof p === 'object' ? p.displayName : p
                );
                const next = m.nextBillingDate ? new Date(m.nextBillingDate) : null;
                return (
                  <tr key={m._id} className="hover:bg-gray-50">
                    <td className="py-3 px-3 align-top">
                      <div className="font-medium text-gray-900">
                        {patient ? `${patient.firstName} ${patient.lastName}` : 'Patient'}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {patient?.phone}{patient?.email ? ` · ${patient.email}` : ''}
                      </div>
                      {patient?.address && (patient.address.city || patient.address.state) && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {[patient.address.city, patient.address.state].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3 align-top">
                      <div className="text-gray-900">Tier {m.tier}</div>
                      <div className="text-xs text-gray-500">
                        {m.productCount} peptide{m.productCount > 1 ? 's' : ''} · ${(m.pricePerCycleCents / 100).toFixed(0)}/{m.cycleLengthDays}d
                      </div>
                    </td>
                    <td className="py-3 px-3 align-top">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_PILL[m.status]}`}>
                        {STATUS_LABEL[m.status]}
                      </span>
                    </td>
                    <td className="py-3 px-3 align-top text-gray-700 text-xs max-w-[260px]">
                      {products.join(', ')}
                    </td>
                    <td className="py-3 px-3 align-top text-gray-700 text-xs">
                      {next ? next.toLocaleDateString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
